/**
 * Les scénarios — des **états de départ**, pas des pages.
 *
 * C'est le renversement décidé le 10 août 2026. Auparavant, chaque expérience
 * était une page avec sa vue, ses réglages et son texte, et changer d'onglet
 * démontait tout : on ne visitait pas un monde, on feuilletait un document. Un
 * scénario ne possède plus rien de tout ça. Il dit seulement **quels corps
 * poser et où** ; le monde, la caméra et l'inspecteur sont les mêmes pour tous
 * et ne s'en vont jamais.
 *
 * ## Ce qui n'a pas été dilué dans le renversement
 *
 * Chaque scénario déclare `confronts` — ce qu'il oppose à des mesures, ou
 * pourquoi il n'oppose rien. Le champ n'est pas optionnel, exactement comme la
 * section de confrontation d'une expérience ne l'était pas (CLAUDE.md §7). Un
 * scénario qui se tairait laisserait croire qu'il démontre quelque chose, et
 * c'est le seul risque que SPEC §10 nomme. Une absence ne se lit pas ; un refus
 * se lit.
 *
 * Aujourd'hui tous refusent, et pour la même raison : ils montrent les
 * conséquences d'une loi qu'on leur a donnée, ce qui ne dit rien du monde.
 *
 * ## Rien n'est tiré au hasard
 *
 * Les configurations sont écrites en dur, pas engendrées : le même scénario
 * donne toujours le même monde, sur toutes les machines (CLAUDE.md §5).
 */

import type { Entity, World } from "../core/world.js";
import { circularSpeed } from "../core/orbit.js";
import { quantity, valueIn } from "../core/units.js";
import { fr } from "../i18n/fr.js";

const AU = 1.496e11;
const SOLAR_MASS = 1.989e30;
const EARTH_MASS = 5.972e24;
const JUPITER_MASS = 1.898e27;

/**
 * Rayons vrais, en mètres — **références externes** (valeurs publiées), que ce
 * projet n'a pas mesurées. Ils n'entrent dans aucun calcul : le moteur ne
 * connaît que des points matériels. Ils servent à dessiner fidèlement, ce qui
 * est la condition pour pouvoir descendre voir un corps de près.
 */
const SOLAR_RADIUS = 6.957e8;
const EARTH_RADIUS = 6.371e6;
const JUPITER_RADIUS = 6.9911e7;
/** Une planète trois fois plus massive : à densité égale, la racine cubique. */
const SUPER_EARTH_RADIUS = EARTH_RADIUS * Math.cbrt(3);

export interface Scenario {
  readonly id: string;
  readonly label: string;
  /** Une ligne : ce qu'on va voir, et ce qui vaut la peine d'être regardé. */
  readonly summary: string;
  /**
   * Ce que ce scénario confronte à des mesures — ou pourquoi il ne confronte
   * rien. Jamais vide. Voir l'en-tête de module.
   */
  readonly confronts: string;
  readonly seed: () => World;
}

// ── Fabrication ──────────────────────────────────────────────────────────────

function entity(
  id: string,
  label: string,
  kind: Entity["kind"],
  mass: number,
  radius: number,
  x: number,
  y: number,
  vx: number,
  vy: number,
): Entity {
  return {
    id,
    label,
    kind,
    mass: quantity(mass, "kg"),
    radius: quantity(radius, "m"),
    position: { x: quantity(x, "m"), y: quantity(y, "m") },
    velocity: { x: quantity(vx, "m/s"), y: quantity(vy, "m/s") },
  };
}

/** Vitesse circulaire, empruntée au noyau plutôt que réécrite ici. */
function circular(centralMass: number, radius: number): number {
  return valueIn(
    circularSpeed(quantity(centralMass, "kg"), quantity(radius, "m")),
    "m/s",
  );
}

function world(entities: readonly Entity[]): World {
  return { entities, elapsed: quantity(0, "s") };
}

/**
 * Un corps sur une orbite circulaire autour d'un centre immobile à l'origine.
 *
 * L'angle de départ n'est pas décoratif. Toutes les planètes posées à zéro
 * s'alignent en file sur un seul rayon, et le système ressemble alors à une
 * liste plutôt qu'à un système — c'est ce que la première capture a montré.
 * Décaler les phases ne change rien à la physique et beaucoup à ce qu'on lit.
 */
function orbiting(
  id: string,
  label: string,
  kind: Entity["kind"],
  mass: number,
  bodyRadius: number,
  centralMass: number,
  orbit: number,
  angle: number,
): Entity {
  const speed = circular(centralMass, orbit);
  return entity(
    id,
    label,
    kind,
    mass,
    bodyRadius,
    orbit * Math.cos(angle),
    orbit * Math.sin(angle),
    -speed * Math.sin(angle),
    speed * Math.cos(angle),
  );
}

// ── Les scénarios ────────────────────────────────────────────────────────────

function system(): World {
  return world([
    entity("etoile", fr.worldStar, "star", SOLAR_MASS, SOLAR_RADIUS, 0, 0, 0, 0),
    orbiting("p1", `${fr.worldPlanet} 1`, "planet", EARTH_MASS, EARTH_RADIUS, SOLAR_MASS, AU, 0.4),
    orbiting(
      "p2",
      `${fr.worldPlanet} 2`,
      "planet",
      3 * EARTH_MASS,
      SUPER_EARTH_RADIUS,
      SOLAR_MASS,
      1.9 * AU,
      2.6,
    ),
    orbiting(
      "p3",
      `${fr.worldPlanet} 3`,
      "planet",
      JUPITER_MASS,
      JUPITER_RADIUS,
      SOLAR_MASS,
      3.4 * AU,
      4.5,
    ),
  ]);
}

/**
 * Deux étoiles autour de leur barycentre, et une planète qui les entoure toutes
 * les deux.
 *
 * Les vitesses sont opposées et les masses égales : le barycentre ne bouge pas,
 * ce qui rend la dérive du moteur immédiatement lisible à l'écran.
 */
function binary(): World {
  const speed = circular(SOLAR_MASS, 4 * AU);
  return world([
    entity("a", `${fr.worldStar} A`, "star", SOLAR_MASS, SOLAR_RADIUS, -AU, 0, 0, -speed),
    entity("b", `${fr.worldStar} B`, "star", SOLAR_MASS, SOLAR_RADIUS, AU, 0, 0, speed),
    orbiting(
      "p1",
      `${fr.worldPlanet} 1`,
      "planet",
      EARTH_MASS,
      EARTH_RADIUS,
      2 * SOLAR_MASS,
      7 * AU,
      1.2,
    ),
  ]);
}

/**
 * Trois masses égales sur un triangle équilatéral, tournant ensemble.
 *
 * C'est la configuration de Lagrange, et elle est **instable** : elle tient
 * quelques tours puis se disloque. Ce n'est pas un défaut du moteur, c'est le
 * problème à trois corps — et le voir arriver vaut mieux que de le lire.
 */
function lagrange(): World {
  const radius = 2 * AU;
  const speed = circular(SOLAR_MASS, radius * Math.sqrt(3));
  return world(
    [0, 1, 2].map((index) => {
      const angle = (2 * Math.PI * index) / 3;
      return entity(
        `l${String(index)}`,
        `${fr.worldStar} ${String(index + 1)}`,
        "star",
        SOLAR_MASS,
        SOLAR_RADIUS,
        radius * Math.cos(angle),
        radius * Math.sin(angle),
        -speed * Math.sin(angle),
        speed * Math.cos(angle),
      );
    }),
  );
}

/**
 * Une étoile de passage traverse un système établi.
 *
 * Le cas où l'intégration à N corps montre ce qu'aucun calcul à deux corps ne
 * pourrait : les planètes ne sont pas seulement déviées, certaines sont
 * éjectées, et laquelle dépend d'où elles se trouvaient au passage. Rien n'est
 * scripté — l'intruse est simplement posée avec sa vitesse, et le reste suit.
 */
function encounter(): World {
  return world([
    entity("etoile", fr.worldStar, "star", SOLAR_MASS, SOLAR_RADIUS, 0, 0, 0, 0),
    orbiting("p1", `${fr.worldPlanet} 1`, "planet", EARTH_MASS, EARTH_RADIUS, SOLAR_MASS, AU, 0.9),
    orbiting(
      "p2",
      `${fr.worldPlanet} 2`,
      "planet",
      JUPITER_MASS,
      JUPITER_RADIUS,
      SOLAR_MASS,
      2.6 * AU,
      3.7,
    ),
    entity(
      "intruse",
      fr.worldIntruder,
      "star",
      0.8 * SOLAR_MASS,
      0.85 * SOLAR_RADIUS,
      -14 * AU,
      9 * AU,
      11000,
      -7000,
    ),
  ]);
}

/** Une étoile, et rien d'autre. C'est à l'utilisateur de peupler le reste. */
function lone(): World {
  return world([entity("etoile", fr.worldStar, "star", SOLAR_MASS, SOLAR_RADIUS, 0, 0, 0, 0)]);
}

export const SCENARIOS: readonly Scenario[] = [
  {
    id: "systeme",
    label: fr.scenarioSystemLabel,
    summary: fr.scenarioSystemSummary,
    confronts: fr.worldNoComparison,
    seed: system,
  },
  {
    id: "binaire",
    label: fr.scenarioBinaryLabel,
    summary: fr.scenarioBinarySummary,
    confronts: fr.worldNoComparison,
    seed: binary,
  },
  {
    id: "lagrange",
    label: fr.scenarioLagrangeLabel,
    summary: fr.scenarioLagrangeSummary,
    confronts: fr.worldNoComparison,
    seed: lagrange,
  },
  {
    id: "rencontre",
    label: fr.scenarioEncounterLabel,
    summary: fr.scenarioEncounterSummary,
    confronts: fr.worldNoComparison,
    seed: encounter,
  },
  {
    id: "seule",
    label: fr.scenarioLoneLabel,
    summary: fr.scenarioLoneSummary,
    confronts: fr.worldNoComparison,
    seed: lone,
  },
];

export function scenarioById(id: string): Scenario | undefined {
  return SCENARIOS.find((one) => one.id === id);
}
